import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import { useSeo } from "@/hooks/useSeo";
import { getByCategory, paginate, sortContent, type SortKey } from "@/lib/content";
import { getCategoryMeta } from "@/mocks/categories";
import CategoryHeader from "@/pages/category/components/CategoryHeader";
import FilterChips from "@/pages/category/components/FilterChips";
import SortTabs from "@/pages/category/components/SortTabs";

const meta = getCategoryMeta("tv-update")!;
const PAGE_SIZE = 6;
const allShows = getByCategory("tv-update");

const initialOptions = Array.from(
  new Set(allShows.map((s) => s.initial).filter((i): i is string => Boolean(i)))
).sort();

const sortOptions = [
  { key: "date-desc", label: "最近更新" },
  { key: "date-asc", label: "最早更新" },
];

export default function TvUpdatePage() {
  const [initial, setInitial] = useState("all");
  const [sort, setSort] = useState<SortKey>("date-desc");
  const [page, setPage] = useState(1);

  useSeo({
    title: "电视剧更新 - 红桃网",
    description:
      "红桃网电视剧更新频道，实时跟进热播剧集更新进度，国剧、美剧、韩剧、日剧一表掌握，再也不错过任何一次追更。",
    keywords: "电视剧更新,追剧,剧集更新,红桃网",
  });

  const result = useMemo(() => {
    const filtered = allShows.filter((s) =>
      initial === "all" ? true : s.initial === initial
    );
    return paginate(sortContent(filtered, sort), page, PAGE_SIZE);
  }, [initial, sort, page]);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 w-full">
        <CategoryHeader meta={meta} />
        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <div className="rounded-xl bg-background-100 border border-background-200 p-4 md:p-5 mb-6 space-y-4">
            <FilterChips
              label="首字母"
              options={initialOptions}
              value={initial}
              allLabel="全部"
              onChange={(v) => {
                setInitial(v);
                setPage(1);
              }}
            />
            <div className="flex items-start gap-3">
              <span className="text-sm text-foreground-500 whitespace-nowrap pt-1.5 w-16 shrink-0">
                排序
              </span>
              <SortTabs
                options={sortOptions}
                value={sort}
                onChange={(k) => {
                  setSort(k as SortKey);
                  setPage(1);
                }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              更新列表
            </h2>
            <span className="text-sm text-foreground-500">
              共 {result.total} 部剧集
            </span>
          </div>

          {result.items.length === 0 ? (
            <EmptyState message="暂无符合当前筛选条件的电视剧，您可以尝试调整首字母筛选。" />
          ) : (
            <div className="space-y-4">
              {result.items.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="group flex flex-col sm:flex-row gap-4 rounded-xl bg-background-50 border border-background-200 p-4 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="w-24 h-32 shrink-0 rounded-lg overflow-hidden mx-auto sm:mx-0">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h3>
                      <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
                        {item.latestEpisode}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800">
                        {item.genre}
                      </span>
                      <span>{item.region}</span>
                      <span>{item.year}</span>
                    </div>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                      {item.summary}
                    </p>
                    <span className="text-xs text-foreground-400 mt-auto pt-3 flex items-center gap-1">
                      <i className="ri-refresh-line" /> 更新于 {item.date}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <Pagination page={result.page} totalPages={result.totalPages} onChange={setPage} />
        </div>
      </main>
      <Footer />
    </div>
  );
}